import { DateTime } from 'luxon';
import { LicensePlateType } from 'dg-web-shared/dto/LicensePlateType.ts';
import * as Flux from 'dg-web-shared/lib/Flux.tsx';
import { isDefined, Maybe, thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import * as ServerStateSlice from 'dg-web-shared/lib/ServerStateSlice.ts';
import * as Http from '../../api/Http.ts';
import * as AsyncRequest from '../../AsyncRequest.ts';

export interface Clearance {
    id: number;
    contractExternalId: string;
    validFrom: DateTime;
    validTo: DateTime;
    permitTypeName: string;
    additionalInfos: string;
    licensePlateNr: Maybe<string>;
    licensePlateType: Maybe<LicensePlateType>;
    licensePlateCountry: Maybe<string>;
    badgeLabelNr: Maybe<string>;
    created: DateTime;
    operatorLoginId: number;
    customerNr: string | null;
}

interface ClearanceUnparsed extends Omit<
    Clearance,
    'validFrom' | 'validTo' | 'created'
> {
    validFrom: string;
    validTo: string;
    created: string;
}

export namespace List {
    const parseClearance = (p: ClearanceUnparsed): Clearance => {
        return {
            id: p.id,
            contractExternalId: p.contractExternalId,
            validFrom: DateTime.fromISO(p.validFrom),
            validTo: DateTime.fromISO(p.validTo),
            permitTypeName: p.permitTypeName,
            additionalInfos: p.additionalInfos,
            licensePlateNr: p.licensePlateNr,
            licensePlateType: p.licensePlateType,
            licensePlateCountry: p.licensePlateCountry,
            badgeLabelNr: p.badgeLabelNr,
            created: DateTime.fromISO(p.created),
            operatorLoginId: p.operatorLoginId,
            customerNr: p.customerNr,
        };
    };

    type UnparsedData = { size: Maybe<number>; data: ClearanceUnparsed[] };
    export type Data = { size: Maybe<number>; data: Clearance[] };

    export type State = ServerStateSlice.ServerState<Data>;

    const shouldFetch = (store: Flux.Store) =>
        Filter.filterActive(Filter.get(store));

    export const { get, reset, setResponse, forceRefetch } =
        ServerStateSlice.generateServerState<Data>(
            'clearance-permit-list-FilteredClearancesState.List',
            () => {
                return { size: null, data: [] };
            },
            (store: Flux.Store, state: State) => {
                if (state.shouldFetch && shouldFetch(store)) {
                    store.update(fetchClearances(store));
                }
            },
            (body: UnparsedData): Data => {
                return { size: body.size, data: body.data.map(parseClearance) };
            },
        );

    const fetchClearances = (store: Flux.Store) =>
        AsyncRequest.request(
            () =>
                Http.OperatorAccount.Clearances.FilteredList.get({
                    permitType: Filter.get(store).permitTypes,
                    issuedBy: thenElse(
                        Filter.get(store).operatorLogins,
                        l =>
                            l.map(id =>
                                id === Filter.OperatorLoginFilterOnlineOption
                                    ? null
                                    : id,
                            ),
                        null,
                    ),
                    issueDateFrom: Filter.get(store).issueDateFrom,
                    issueDateTo: Filter.get(store).issueDateTo,
                    validDateFrom: Filter.get(store).validDateFrom,
                    validDateTo: Filter.get(store).validDateTo,
                    sortField: 'created',
                    withOrWithoutPermits: Filter.get(store).permits,
                }),
            setResponse,
        );
}

export namespace Filter {
    export const OperatorLoginFilterOnlineOption = -99;

    export interface State {
        filterSlideInVisible: boolean;

        permitTypes: number[] | null;
        permitTypeSelectionVisible: boolean;

        operatorLogins: Maybe<number[]>;
        operatorLoginSelectionVisible: boolean;

        issueDateFrom: Maybe<DateTime>;
        issueDateTo: Maybe<DateTime>;
        validDateFrom: Maybe<DateTime>;
        validDateTo: Maybe<DateTime>;
        issueDateSelectionVisible: boolean;
        validDateSelectionVisible: boolean;
        permits: string | null;
        permitsSelectionVisible: boolean;
    }

    export const { set, get, reset, stateWrite } = Flux.generateState<State>(
        'clearance-permit-list-FilteredClearancesState.Filter',
        {
            filterSlideInVisible: false,

            permitTypes: null,
            permitTypeSelectionVisible: false,

            operatorLogins: null,
            operatorLoginSelectionVisible: false,

            issueDateFrom: null,
            issueDateTo: null,
            validDateFrom: null,
            validDateTo: null,
            issueDateSelectionVisible: false,
            validDateSelectionVisible: false,
            permits: null,
            permitsSelectionVisible: false,
        },
    );

    export const permitTypeFilterActive = (s: State): boolean =>
        thenElse(s.permitTypes, pt => pt.length > 0, false);

    export const operatorLoginFilterActive = (s: State): boolean =>
        thenElse(s.operatorLogins, ol => ol.length > 0, false);

    export const issueDateFilterActive = (s: State): boolean =>
        isDefined(s.issueDateFrom) || isDefined(s.issueDateTo);

    export const permitsFilterActive = (s: State): boolean =>
        isDefined(s.permits);

    export const validDateFilterActive = (s: State): boolean =>
        isDefined(s.validDateFrom) || isDefined(s.validDateTo);
    export const filterActive = (s: State): boolean =>
        permitTypeFilterActive(s) ||
        operatorLoginFilterActive(s) ||
        issueDateFilterActive(s) ||
        permitsFilterActive(s) ||
        validDateFilterActive(s);
}

export namespace FilterSummary {
    export interface State {
        hover: boolean;
    }

    export const { set, get, reset, stateWrite } = Flux.generateState<State>(
        'clearance-permit-list-FilteredClearancesState.FilterSummary',
        {
            hover: false,
        },
    );
}
